import React, { useCallback, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import { Button, Chip, IconButton, Stack } from "@mui/material";
import { FiltersChip } from "components";
import { Menu, MenuProps } from "components/popups";
import { useFormatDateHook } from "hooks";
import { FileRepoFilters } from "store/types";

import { FiltersDrawer } from "../Filters-drawer/FiltersDrawer";
import { ColumnInfo, ColumnManagerPopover } from "./ColumnManagerPopover";

interface Props {
  filters: FileRepoFilters;
  setFilters: (data: Partial<FileRepoFilters> | null) => void;
  documentTypes: string[];
  disabled?: boolean;
  columnVisibility: Record<string, boolean>;
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void;
  allColumns: ColumnInfo[];
  actionMenuOptions: MenuProps["options"];
}

type Field = { key: keyof FileRepoFiltersArrs; title: string };

const dumbFilterTitles: Record<keyof FileRepoFiltersArrs, Field> = {
  file_types: { title: "File Type", key: "file_types" },
  investors: { title: "Investor", key: "investors" },
  deals: { title: "Deal", key: "deals" },
  document_types: { title: "Classification", key: "document_types" },
  source: { title: "Source", key: "source" }
};

type FileRepoFiltersArrs = Pick<FileRepoFilters, "investors" | "deals" | "document_types" | "source" | "file_types">;
type OnRemoveOption = {
  field: Field;
  option:
    | {
        id: number;
        name?: string;
        title?: string;
      }
    | string;
};

const limitTags = 5;

export const FilesFilters: React.FC<Props> = ({
  filters,
  setFilters,
  disabled,
  documentTypes,
  columnVisibility,
  onColumnVisibilityChange,
  allColumns,
  actionMenuOptions
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [columnManagerAnchor, setColumnManagerAnchor] = useState<HTMLElement | null>(null);

  const format = useFormatDateHook();

  const isFiltered = useMemo(() => {
    const { ...rest } = filters;
    return Object.values(rest).some((item) => (Array.isArray(item) ? item.length : !!item));
  }, [filters]);

  const pickFilters: FileRepoFiltersArrs = useMemo(() => {
    const { deals, investors, document_types, source, file_types } = filters;
    return { file_types, deals, investors, document_types, source };
  }, [filters]);

  const onRemoveField = useCallback(
    (field: Field) => {
      setFilters({ ...filters, [field.key]: [] });
    },
    [filters, setFilters]
  );

  const onRemoveOption = useCallback(
    ({ field, option }: OnRemoveOption) => {
      setFilters({
        ...filters,
        [field.key]: filters[field.key].filter((item) => {
          if (typeof item === "object" && typeof option === "object") {
            return item.id !== option.id;
          } else {
            return item !== option;
          }
        })
      });
    },
    [filters, setFilters]
  );

  const handleToggleColumn = useCallback(
    (columnId: string) => {
      onColumnVisibilityChange({
        ...columnVisibility,
        [columnId]: columnVisibility[columnId] === false ? true : false
      });
    },
    [columnVisibility, onColumnVisibilityChange]
  );

  const dateFrom: string = filters.date?.from ? `From: ${format({ date: filters.date.from })}` : "";
  const dateTo: string = filters.date?.to ? `To: ${format({ date: filters.date.to })}` : "";
  const divider: string = filters.date && Object.keys(filters.date).length > 2 ? "-" : "";

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
      {/* LEFT SIDE - Filters */}
      <Stack direction="row" gap="15px" alignItems="center" sx={{ overflow: "hidden", flex: 1 }}>
        <Button
          disabled={disabled}
          sx={{
            height: "28px",
            fontSize: "12px",
            textTransform: "none",
            fontWeight: "400",
            borderRadius: "32px",
            borderColor: "#7b92a9",
            color: "#496682",
            backgroundColor: isFiltered ? "#dae0e2" : "transparent",
            whiteSpace: "nowrap",
            minWidth: "auto",
            padding: "4px 8px",
            gap: "4px"
          }}
          onClick={() => setIsDrawerOpen(true)}
          variant="outlined"
          startIcon={<FilterAltIcon sx={{ fontSize: "16px !important" }} />}
        >
          Filter
        </Button>

        <Stack
          direction="row"
          gap="4px"
          alignItems="center"
          sx={{ cursor: "pointer" }}
          onClick={() => setIsDrawerOpen(true)}
        >
          <IconButton
            size="sm"
            sx={{
              padding: "4px",
              background:
                "linear-gradient(90deg, rgba(255, 255, 255, 0.96) 0%, rgba(255, 255, 255, 0.96) 100%), linear-gradient(90deg, rgb(7, 31, 53) 0%, rgb(7, 31, 53) 100%)"
            }}
          >
            <AddIcon sx={{ fontSize: 16, color: "#071f35" }} />
          </IconButton>
          <span style={{ fontSize: "12px", color: "#071f35", whiteSpace: "nowrap" }}>Add filter</span>
        </Stack>

        {/* Active filter chips */}
        {Object.keys(pickFilters)
          .slice(0 - limitTags)
          .map((field) => (
            <FiltersChip
              key={field}
              field={dumbFilterTitles[field as keyof FileRepoFiltersArrs]}
              options={filters[field as keyof FileRepoFiltersArrs] as any}
              renderOption={(option) => (typeof option === "object" ? (option.name! ?? option.title) : option)}
              removeFieldOption={onRemoveOption}
              onRemove={onRemoveField}
              renderField={(field) => field.title}
            />
          ))}
        {filters.date && (
          <Chip
            sx={{ height: "26px", fontSize: "12px", color: "#071f35" }}
            label={`${dateFrom} ${divider} ${dateTo}`}
            onDelete={() => setFilters({ ...filters, date: null })}
          />
        )}
        {Boolean(filters.show_latest) && (
          <Chip
            sx={{ height: "26px", fontSize: "12px", color: "#071f35" }}
            label="Show Latest"
            onDelete={() => setFilters({ ...filters, show_latest: false })}
          />
        )}
        {isFiltered && (
          <Button
            disabled={disabled}
            onClick={() => setFilters(null)}
            startIcon={<CloseIcon className="icon" />}
            sx={{ fontSize: "12px", textTransform: "none", whiteSpace: "nowrap" }}
          >
            Clear All Filters
          </Button>
        )}
      </Stack>
      {/* RIGHT SIDE - Column manager + Actions */}
      <Stack direction="row" gap="10px" alignItems="center" sx={{ flexShrink: 0 }}>
        <IconButton size="sm" sx={{ padding: "4px" }} onClick={(e) => setColumnManagerAnchor(e.currentTarget)}>
          <ViewWeekOutlinedIcon sx={{ fontSize: 16, color: "#071f35" }} />
        </IconButton>
        <Menu options={actionMenuOptions} />
      </Stack>
      <ColumnManagerPopover
        anchorEl={columnManagerAnchor}
        onClose={() => setColumnManagerAnchor(null)}
        columns={allColumns}
        columnVisibility={columnVisibility}
        onToggle={handleToggleColumn}
      />
      <FiltersDrawer
        filters={filters}
        setFilters={setFilters}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        documentTypes={documentTypes}
      />
    </Stack>
  );
};
